import { memo, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  CreditCard,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Network,
  Ticket,
  Users,
  X,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useNotificationStore,
  type Notification,
  type NotificationType,
  type NotificationCategory,
} from '@/stores/notification.store';
import { formatRelativeTime } from '@/lib/time';

/* ── Icon helpers ─────────────────────────────────────────────── */

const typeIcon: Record<NotificationType, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
};

const typeColor: Record<NotificationType, string> = {
  info: 'text-primary bg-primary/10',
  success: 'text-emerald-600 bg-emerald-500/10 dark:text-emerald-400',
  warning: 'text-amber-600 bg-amber-500/10 dark:text-amber-400',
  error: 'text-red-600 bg-red-500/10 dark:text-red-400',
};

const categoryIcon: Record<NotificationCategory, React.ElementType> = {
  system: Settings,
  billing: CreditCard,
  network: Network,
  subscriber: Users,
  ticket: Ticket,
};

/* ── Single notification row ──────────────────────────────────── */

interface NotificationRowProps {
  notification: Notification;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onClose: () => void;
}

const NotificationRow = memo(function NotificationRow({
  notification: n,
  onRead,
  onDismiss,
  onClose,
}: NotificationRowProps) {
  const TypeIcon = typeIcon[n.type];
  const CatIcon = categoryIcon[n.category];
  const relative = formatRelativeTime(n.createdAt);

  const handleClick = () => {
    if (!n.read) onRead(n.id);
    if (n.href) onClose();
  };

  const content = (
    <div
      className={cn(
        'group relative flex gap-3 rounded-lg px-3 py-2.5 transition-colors',
        !n.read && 'bg-primary/[0.03] dark:bg-primary/[0.06]',
        n.href ? 'cursor-pointer hover:bg-accent' : 'hover:bg-accent/50',
      )}
      onClick={!n.href ? handleClick : undefined}
      role="menuitem"
      tabIndex={-1}
      aria-label={`${n.read ? '' : 'Unread: '}${n.title}`}
    >
      {/* Unread dot */}
      {!n.read && (
        <span className="absolute left-1 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary" />
      )}

      {/* Icon */}
      <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', typeColor[n.type])}>
        <TypeIcon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn('text-sm leading-snug', !n.read ? 'font-semibold text-foreground' : 'font-medium text-foreground/80')}>
            {n.title}
          </p>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss(n.id);
            }}
            className="shrink-0 rounded p-0.5 text-muted-foreground/40 opacity-0 transition-all hover:bg-accent hover:text-foreground group-hover:opacity-100"
            aria-label="Dismiss notification"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground line-clamp-2">
          {n.message}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <CatIcon className="h-3 w-3 text-muted-foreground/50" />
          <span className="text-[11px] capitalize text-muted-foreground/60">{n.category}</span>
          <span className="text-muted-foreground/30">·</span>
          <span className="text-[11px] text-muted-foreground/60">{relative}</span>
        </div>
      </div>

      {/* Mark-as-read button */}
      {!n.read && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRead(n.id);
          }}
          className="mt-0.5 shrink-0 rounded p-1 text-muted-foreground/40 opacity-0 transition-all hover:bg-accent hover:text-primary group-hover:opacity-100"
          aria-label="Mark as read"
          title="Mark as read"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  if (n.href) {
    return (
      <Link to={n.href} onClick={handleClick} className="block">
        {content}
      </Link>
    );
  }

  return content;
});

/* ── Main dropdown ────────────────────────────────────────────── */

export const NotificationDropdown = memo(function NotificationDropdown() {
  const { notifications, isOpen, toggle, close, markAsRead, markAllAsRead, dismiss } =
    useNotificationStore();
  const unreadCount = useNotificationStore((s) => s.unreadCount());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  // Close on Escape + arrow key navigation
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = dropdownRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
        if (!items || items.length === 0) return;
        const currentIndex = Array.from(items).findIndex((el) => el === document.activeElement || el.contains(document.activeElement as Node));
        let nextIndex: number;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }
        items[nextIndex]?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleRead = useCallback((id: string) => markAsRead(id), [markAsRead]);
  const handleDismiss = useCallback((id: string) => dismiss(id), [dismiss]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={toggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          'relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
          isOpen && 'bg-accent text-foreground',
        )}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white animate-fade-in">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="absolute right-0 mt-1 w-[380px] animate-dialog-in rounded-xl border bg-popover shadow-xl"
          role="menu"
          aria-label="Notifications"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
              {unreadCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-[420px] overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <BellOff className="h-8 w-8 text-muted-foreground/30" />
                <p className="mt-3 text-sm font-medium">No notifications</p>
                <p className="mt-1 text-xs text-muted-foreground/60">
                  You&apos;re all caught up!
                </p>
              </div>
            ) : (
              <div className="p-1.5">
                {notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onRead={handleRead}
                    onDismiss={handleDismiss}
                    onClose={close}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t px-4 py-2.5">
              <Link
                to="/notifications"
                onClick={close}
                className="block text-center text-xs font-medium text-primary transition-colors hover:text-primary/80"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
